import {
  createStyles,
  makeStyles,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  CircularProgress,
  Link,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { PayerTransaction, PayerTransactionConnection, PaymentStatus } from '../gql-types.generated';
import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import TableHeader, { HeaderCell } from './TableHeader';
import { paymentStatusDisplay } from '../util/PaymentStatus';
import { GetApp } from '@material-ui/icons';

const useStylesMobile = makeStyles(() =>
  createStyles({
    table: {
      overflow: 'auto',
      margin: '0px',
    },
    tableWrap: {
      maxHeight: '75vh',
      overflow: 'auto',
    },
    cellData: {
      display: 'flex',
      padding: '2px',
    },
    cellDetails: {
      width: '75%',
      fontSize: '16px',
    },
    cellDateStatus: {
      fontSize: '14px',
      margin: '2px 0px 0px auto',
      width: '25%',
      textAlign: 'right',
    },
    cellReferenceNumber: {
      color: '#276EF1',
      wordBreak: 'break-word',
      marginTop: '2px',
    },
    cellAmount: {
      color: '#000000DE',
    },
    emptyMessage: {
      fontSize: '14px',
      textAlign: 'center',
      fontStyle: 'oblique',
    },
    loadingSpinner: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    errorMessage: {
      fontSize: '18px',
      textAlign: 'center',
      fontStyle: 'oblique',
      color: 'red',
    },
  }),
);

const useStyles = makeStyles(() =>
  createStyles({
    table: {
      overflow: 'auto',
      margin: '0px',
      minWidth: 1000,
    },
    tableWrap: {
      maxHeight: '55vh',
      overflow: 'auto',
    },
    tableCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      padding: '16px 4px',
    },
    emptyMessage: {
      fontSize: '14px',
      textAlign: 'center',
      fontStyle: 'oblique',
    },
    loadingSpinner: {
      height: 60,
      width: 60,
      display: 'inline-block',
    },
    errorMessage: {
      fontSize: '18px',
      textAlign: 'center',
      fontStyle: 'oblique',
      color: 'red',
    },
    referenceCell: {
      fontSize: 16,
      wordWrap: 'break-word',
      color: '#276EF1',
      wordBreak: 'break-word',
    },
  }),
);

// Table for the recent transactions list
interface RecentTransactionsListProps {
  platform: string;
  recentTransactions: PayerTransaction[] | undefined;
  recentTransactionsConnection: PayerTransactionConnection | undefined;
  loadPage: () => void;
  showRecentPaymentDetails: (paymentDetails: PayerTransaction | null | undefined) => void;
  recentPaymentsError: Error | undefined;
  creditMemosEnabled: boolean;
}

const RecentTransactionsList: React.FC<RecentTransactionsListProps> = props => {
  const {
    platform,
    recentTransactions,
    recentTransactionsConnection,
    loadPage,
    showRecentPaymentDetails,
    recentPaymentsError,
    creditMemosEnabled,
  } = props;

  const recentTransactionsHeaderCells: HeaderCell[] = [
    { id: 'reference', label: 'Reference #' },
    { id: 'invoice', label: 'Download Invoice', align: 'center' },
    { id: 'date', label: 'Date Paid' },
    { id: 'paid', label: 'Paid', align: 'right' },
    ...(creditMemosEnabled ? [{ id: 'credit', label: 'Credits Applied', align: 'right' }] : []),
    { id: 'discount', label: 'Discount', align: 'right' },
    { id: 'amount', label: 'Total', align: 'right' },
    { id: 'action', label: '' },
  ];

  const classes = useStyles();
  const classesMobile = useStylesMobile();

  const emptyMessage = 'No payments completed recently.';
  const errorMessage = 'Unable to load data. Please try again later.';

  const skeletonMobile: JSX.Element[] = [];
  for (let i = 0; i <= recentTransactionsHeaderCells.length; i += 1) {
    skeletonMobile.push(
      <TableRow>
        <TableCell>
          <div style={{ display: 'flex' }}>
            <div style={{ width: '80%' }}>
              <Skeleton height="1.5em" width="8em" />
              <Skeleton height="1.5em" width="6em" />
              <Skeleton height="1.5em" width="4em" />
            </div>
            <div style={{ width: '20%', textAlign: 'right', alignItems: 'end' }}>
              <Skeleton height="1.5em" width="5em" />
              <Skeleton height="1.5em" width="5em" />
            </div>
          </div>
        </TableCell>
      </TableRow>,
    );
  }

  const skeletonDesktop: JSX.Element[] = [];
  for (let i = 0; i <= recentTransactionsHeaderCells.length; i += 1) {
    skeletonDesktop.push(
      <TableRow key={i}>
        <TableCell style={{ paddingLeft: '35px' }} className={classes.tableCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell className={classes.tableCell}>
          <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto', marginRight: 'auto' }} />
        </TableCell>
        <TableCell className={classes.tableCell}>
          <Skeleton height="1.5em" width="80%" />
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        {creditMemosEnabled && (
          <TableCell align="right" className={classes.tableCell}>
            <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
          </TableCell>
        )}
        <TableCell align="right" className={classes.tableCell}>
          <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        <TableCell align="right" className={classes.tableCell}>
          <Skeleton height="1.5em" width="50%" style={{ marginLeft: 'auto' }} />
        </TableCell>
        <TableCell />
      </TableRow>,
    );
  }

  const getColumns = () => {
    if (creditMemosEnabled) {
      return (
        <>
          <col width="22%" />
          <col width="15%" />
          <col width="8%" />
          <col width="13%" />
          <col width="13%" />
          <col width="13%" />
          <col width="13%" />
          <col width="3%" />
        </>
      );
    }
    return (
      <>
        <col width="24%" />
        <col width="20%" />
        <col width="8%" />
        <col width="15%" />
        <col width="15%" />
        <col width="15%" />
        <col width="3%" />
      </>
    );
  };

  if (platform === 'mobile') {
    return (
      <TableContainer className={classesMobile.tableWrap} data-cy="recent-transactions-list">
        <InfiniteScroll
          pageStart={0}
          loadMore={loadPage}
          hasMore={recentTransactionsConnection?.pageInfo.hasNextPage as boolean}
          loader={
            <div className={classesMobile.loadingSpinner}>
              <CircularProgress key={0} size={42} />
            </div>
          }
          initialLoad={false}
          useWindow={false}
        >
          <Table className={classesMobile.table} aria-label="Recent Transactions List" data-cy="recent-payments-table" stickyHeader>
            <TableBody>
              {recentPaymentsError && (
                <TableRow>
                  <TableCell variant="footer" size="small" className={classesMobile.errorMessage}>
                    {errorMessage}
                  </TableCell>
                </TableRow>
              )}
              {!recentPaymentsError && !recentTransactions && <>{skeletonMobile}</>}
              {!recentPaymentsError && recentTransactions?.length === 0 && (
                <TableRow>
                  <TableCell variant="footer" size="small" className={classesMobile.emptyMessage}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              )}

              {!recentPaymentsError &&
                recentTransactions?.map(transaction => (
                  <TableRow key={transaction.paymentRequest?.id} hover>
                    <TableCell>
                      <div className={classesMobile.cellData}>
                        <div className={classesMobile.cellDetails}>
                          <div className={classesMobile.cellReferenceNumber}>
                            <Link
                              href="#"
                              onClick={() => {
                                showRecentPaymentDetails(transaction);
                              }}
                            >
                              {transaction.paymentRequest?.referenceNumber}
                            </Link>
                          </div>
                          <div className={classesMobile.cellAmount}>
                            {typeof transaction.paymentRequest?.totalPaid === 'number'
                              ? new Intl.NumberFormat('en', {
                                  style: 'currency',
                                  currency: transaction.payment?.currency || 'USD',
                                  currencyDisplay: 'symbol',
                                }).format(transaction.paymentRequest?.totalPaid / 100)
                              : null}
                          </div>
                        </div>
                        <div className={classesMobile.cellDateStatus}>
                          {transaction.payment?.completedTimestamp && (
                            <div>{new Date(transaction.payment.completedTimestamp).toLocaleDateString()}</div>
                          )}
                          <div>
                            {
                              paymentStatusDisplay.find(item => {
                                return item.name === transaction.paymentRequest?.status;
                              })?.display
                            }
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </InfiniteScroll>
      </TableContainer>
    );
  }

  return (
    <TableContainer className={classes.tableWrap} data-cy="recent-transactions-list">
      <InfiniteScroll
        pageStart={0}
        loadMore={loadPage}
        hasMore={recentTransactionsConnection?.pageInfo.hasNextPage as boolean}
        loader={
          <div className={classes.loadingSpinner}>
            <CircularProgress key={0} size={42} />
          </div>
        }
        initialLoad={false}
        useWindow={false}
      >
        <Table className={classes.table} aria-label="Recent Transactions List" data-cy="recent-payments-table" stickyHeader>
          {getColumns()}
          <TableHeader cells={recentTransactionsHeaderCells} isPaymentsDue={false} />
          <TableBody>
            {recentPaymentsError && (
              <TableRow>
                <TableCell variant="footer" size="small" colSpan={5} className={classes.errorMessage}>
                  {errorMessage}
                </TableCell>
              </TableRow>
            )}
            {!recentPaymentsError && !recentTransactions && (platform === 'mobile' ? <>{skeletonMobile}</> : <>{skeletonDesktop}</>)}
            {!recentPaymentsError && recentTransactions?.length === 0 && (
              <TableRow>
                <TableCell variant="footer" size="small" colSpan={5} className={classes.emptyMessage}>
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}

            {!recentPaymentsError &&
              recentTransactions?.map(transaction => {
                const lastPaymentDate = transaction.payment?.completedTimestamp;
                const discountEndDate = transaction.paymentRequest?.discountEndDate;
                const paymentRequestCompletedBeforeDiscountEndDate = new Date(lastPaymentDate) < new Date(discountEndDate);
                const currencyFormatter = new Intl.NumberFormat('en', {
                  style: 'currency',
                  currency: transaction.payment?.currency || 'USD',
                  currencyDisplay: 'symbol',
                });

                let paidAmount = 0;
                let creditAmount = 0;
                transaction.paymentRequest?.payments.forEach(transactionPayment => {
                  if (transactionPayment?.status === PaymentStatus.Completed || transactionPayment?.status === PaymentStatus.Pending) {
                    paidAmount += transactionPayment?.amount || 0;
                    creditAmount += transactionPayment?.creditAmount || 0;
                  }
                });

                return (
                  <TableRow key={transaction.paymentRequest?.id} hover>
                    <TableCell align="left" style={{ paddingLeft: '35px' }} className={classes.referenceCell}>
                      <Link
                        href="#"
                        onClick={() => {
                          showRecentPaymentDetails(transaction);
                        }}
                      >
                        {transaction.paymentRequest?.referenceNumber}
                      </Link>
                    </TableCell>

                    <TableCell align="center" className={classes.tableCell}>
                      {transaction.paymentRequest?.invoiceLink ? (
                        <Link href={transaction.paymentRequest.invoiceLink}>
                          <GetApp />
                        </Link>
                      ) : (
                        '-- --'
                      )}
                    </TableCell>

                    <TableCell className={classes.tableCell}>
                      {transaction.payment?.completedTimestamp
                        ? new Date(transaction.payment.completedTimestamp).toLocaleDateString()
                        : ''}
                    </TableCell>

                    <TableCell className={classes.tableCell} align="right">
                      {currencyFormatter.format(paidAmount / 100)}
                    </TableCell>

                    {creditMemosEnabled && (
                      <TableCell className={classes.tableCell} align="right">
                        {currencyFormatter.format(creditAmount / 100)}
                      </TableCell>
                    )}

                    <TableCell className={classes.tableCell} align="right">
                      {paymentRequestCompletedBeforeDiscountEndDate
                        ? currencyFormatter.format(
                            typeof transaction.paymentRequest?.discountAmount === 'number'
                              ? transaction.paymentRequest.discountAmount / 100
                              : 0,
                          )
                        : currencyFormatter.format(0)}
                    </TableCell>

                    <TableCell className={classes.tableCell} align="right">
                      {currencyFormatter.format((transaction.paymentRequest?.amount || 0) / 100)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </InfiniteScroll>
    </TableContainer>
  );
};

export default RecentTransactionsList;
